// Calculs de l'onglet Factures : composition d'une pièce, reste à régler, période.
import type { LineNature } from './lineTotals';

const NATURE_ORDER: LineNature[] = ['honoraires', 'frais', 'provision'];

export interface NatureRow {
  billing_document_id: string;
  nature: string;
  ht_total: number | string;
}

export interface NatureAmount {
  nature: LineNature;
  ht: number;
}

/** HT par nature pour chaque pièce, sans les natures à zéro. */
export function compositionByDocument(rows: NatureRow[]): Map<string, NatureAmount[]> {
  const cents = new Map<string, Map<LineNature, number>>();
  for (const row of rows) {
    if (!NATURE_ORDER.includes(row.nature as LineNature)) continue;
    const byNature = cents.get(row.billing_document_id) ?? new Map<LineNature, number>();
    const nature = row.nature as LineNature;
    byNature.set(nature, (byNature.get(nature) ?? 0) + Math.round(Number(row.ht_total) * 100));
    cents.set(row.billing_document_id, byNature);
  }
  const result = new Map<string, NatureAmount[]>();
  for (const [id, byNature] of cents) {
    const amounts = NATURE_ORDER.filter((n) => (byNature.get(n) ?? 0) !== 0).map((n) => ({
      nature: n,
      ht: (byNature.get(n) ?? 0) / 100,
    }));
    if (amounts.length > 0) result.set(id, amounts);
  }
  return result;
}

export interface PayableDoc {
  doc_type: string;
  status: string;
  ttc_total: number | string;
  number?: string | null;
  polar_invoice_id?: string | null;
}

/**
 * Ce que le client doit encore sur une facture émise. null quand la question ne se pose
 * pas (brouillon, pièce annulée, devis, avoir). `paid` = montant déjà encaissé.
 * Une facture figée dans Polar (numérotée, pas encore envoyée) est émise : elle est due.
 */
export function remainingToPay(doc: PayableDoc, paid = 0): number | null {
  if (doc.doc_type !== 'invoice') return null;
  if (doc.status === 'paid') return 0;
  const frozenInPolar = doc.status === 'draft' && !!doc.number && !!doc.polar_invoice_id;
  if (doc.status !== 'sent' && doc.status !== 'overdue' && !frozenInPolar) return null;
  const restCents = Math.round(Number(doc.ttc_total) * 100) - Math.round(paid * 100);
  return Math.max(restCents, 0) / 100;
}

export type BillingPeriod = 'month' | 'last_month' | 'year' | 'all';

export const BILLING_PERIOD_LABELS: Record<BillingPeriod, string> = {
  month: 'Ce mois-ci',
  last_month: 'Le mois dernier',
  year: 'Cette année',
  all: 'Toutes les dates',
};

/** Date d'émission, ou date de création pour un brouillon. Le mois et l'année suffisent. */
export function matchesBillingPeriod(
  doc: { issue_date: string | null; created_at: string },
  period: BillingPeriod,
  now: Date,
): boolean {
  if (period === 'all') return true;
  const iso = doc.issue_date ?? doc.created_at;
  const date = doc.issue_date ? new Date(`${iso.slice(0, 10)}T12:00:00`) : new Date(iso);
  const year = date.getFullYear();
  const month = date.getMonth();
  if (period === 'year') return year === now.getFullYear();
  const ref = period === 'month' ? now : new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return year === ref.getFullYear() && month === ref.getMonth();
}

export interface SummaryDoc extends PayableDoc {
  id: string;
  ht_total: number | string;
}

export interface InvoicesSummary {
  /** Pièces comptées : factures et avoirs exclus des devis et des pièces annulées. */
  count: number;
  ht: number;
  vat: number;
  ttc: number;
  /** HT par nature, part du HT total en pourcentage arrondi. */
  byNature: { nature: LineNature; ht: number; share: number }[];
  collections: {
    /** Encaissé. */
    paid: number;
    /** Impayé, non échu (factures figées dans Polar comprises). */
    due: number;
    /** Impayé échu. */
    late: number;
    /** Brouillons non figés. */
    drafts: number;
  };
}

/** Aperçu des pièces affichées : les devis, avoirs et pièces annulées ne comptent pas. */
export function summarizeInvoices(
  docs: SummaryDoc[],
  composition: Map<string, NatureAmount[]>,
  paidByDoc: Map<string, number> = new Map(),
): InvoicesSummary {
  const counted = docs.filter((d) => d.doc_type === 'invoice' && d.status !== 'void');
  const c = (v: number | string) => Math.round(Number(v) * 100);
  let ht = 0;
  let ttc = 0;
  const natureCents = new Map<LineNature, number>(NATURE_ORDER.map((n) => [n, 0]));
  const col = { paid: 0, due: 0, late: 0, drafts: 0 };
  for (const doc of counted) {
    ht += c(doc.ht_total);
    ttc += c(doc.ttc_total);
    for (const part of composition.get(doc.id) ?? []) {
      natureCents.set(part.nature, (natureCents.get(part.nature) ?? 0) + c(part.ht));
    }
    const paid = paidByDoc.get(doc.id) ?? 0;
    const rest = remainingToPay(doc, paid);
    if (rest === null) {
      col.drafts += c(doc.ttc_total);
      continue;
    }
    col.paid += c(doc.ttc_total) - c(rest);
    if (doc.status === 'overdue') col.late += c(rest);
    else col.due += c(rest);
  }
  return {
    count: counted.length,
    ht: ht / 100,
    vat: (ttc - ht) / 100,
    ttc: ttc / 100,
    byNature: NATURE_ORDER.map((nature) => {
      const cents = natureCents.get(nature) ?? 0;
      return { nature, ht: cents / 100, share: ht > 0 ? Math.round((cents / ht) * 100) : 0 };
    }),
    collections: { paid: col.paid / 100, due: col.due / 100, late: col.late / 100, drafts: col.drafts / 100 },
  };
}
